import { AxiosError } from 'axios';
import toaster from 'react-hot-toast';
import {
  BulkDeviceAction,
  BulkDeviceActionErrorMessage,
  BulkDeviceActionLabel,
  type WashboardDevice,
} from 'api/admin/washboard/washboard';
import { useDownloadLogsWashboardDevice } from 'api/queries/useDownloadLogsWashboardDevice';
import { useRebootWashboardDevice } from 'api/queries/useRebootWashboardDevice';
import { useReinitializeWashboardDevice } from 'api/queries/useReinitializeWashboardDevice';

interface UseWashboardBulkActionsParams {
  selectedDevices: WashboardDevice[];
  onComplete: () => void;
}

export function useWashboardBulkActions({
  selectedDevices,
  onComplete,
}: UseWashboardBulkActionsParams) {
  const reinitializeMutation = useReinitializeWashboardDevice();
  const rebootMutation = useRebootWashboardDevice();
  const downloadLogsMutation = useDownloadLogsWashboardDevice();

  const runBulkDeviceAction = async (action: BulkDeviceAction) => {
    let actionLabel: string;
    let defaultErrorMessage: string;

    switch (action) {
      case BulkDeviceAction.Reboot:
        actionLabel = BulkDeviceActionLabel.Reboot;
        defaultErrorMessage = BulkDeviceActionErrorMessage.Reboot;
        break;
      case BulkDeviceAction.Reinitialize:
        actionLabel = BulkDeviceActionLabel.Reinitialize;
        defaultErrorMessage = BulkDeviceActionErrorMessage.Reinitialize;
        break;
      case BulkDeviceAction.DownloadLogs:
        actionLabel = BulkDeviceActionLabel.DownloadLogs;
        defaultErrorMessage = BulkDeviceActionErrorMessage.DownloadLogs;
        break;
    }

    const results = await Promise.allSettled(
      selectedDevices.map((device) => {
        switch (action) {
          case BulkDeviceAction.Reboot:
            return rebootMutation.mutateAsync({
              deviceId: device.id,
              deviceName: device.name,
              skipToast: true,
            });
          case BulkDeviceAction.Reinitialize:
            return reinitializeMutation.mutateAsync({
              deviceId: device.id,
              deviceName: device.name,
              skipToast: true,
            });
          case BulkDeviceAction.DownloadLogs:
            return downloadLogsMutation.mutateAsync({
              deviceId: device.id,
              deviceName: device.name,
              skipToast: true,
            });
        }
      }),
    );

    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const errors = new Map<string, string[]>();

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        const error = result.reason as AxiosError<{ error: string }>;
        const errorMessage = error?.response?.data?.error ?? defaultErrorMessage;
        const deviceName = selectedDevices[index].name;

        if (!errors.has(errorMessage)) {
          errors.set(errorMessage, []);
        }
        errors.get(errorMessage)?.push(deviceName);
      }
    });

    if (successCount > 0) {
      toaster(`${actionLabel} succeeded for ${successCount} device${successCount > 1 ? 's' : ''}`, {
        icon: '✅',
      });
    }

    if (errors.size > 0) {
      const errorLines = Array.from(errors.entries()).map(
        ([errorMessage, deviceNames]) => `• ${errorMessage}: ${deviceNames.join(', ')}`,
      );
      toaster(errorLines.join('\n'), { icon: '❌', duration: 8000 });
    }

    onComplete();
  };

  return {
    reinitializeMutation,
    rebootMutation,
    downloadLogsMutation,
    handleReinitialize: () => runBulkDeviceAction(BulkDeviceAction.Reinitialize),
    handleReboot: () => runBulkDeviceAction(BulkDeviceAction.Reboot),
    handleDownloadLogs: () => runBulkDeviceAction(BulkDeviceAction.DownloadLogs),
  };
}
